import { useTranslation } from '../../i18n';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, JalaliDatePicker, Switch } from '@parsina/framework';
import { C } from '../../constants/theme';
import { useShortcut } from '../../lib/shortcuts.jsx';
import { validateReferralLetter } from '../../lib/insurance';
import { useInsurerName } from '../../hooks/useInsurance';
import AdaptiveModal from './AdaptiveModal';

/**
 * «ثبت معرفی‌نامه» — registering one insurer's authorisation to treat this
 * patient here.
 *
 * ONE dialog, opened from three places that all mean the same act: the patient
 * drawer (any time), the booking modal (before the visit), and check-in (where a
 * missing letter is usually discovered). Three copies of this form would be three
 * chances for them to disagree about what a letter is.
 *
 * `policyId` may be pre-selected by the caller — the desk clicked «ثبت» on a
 * specific insurer's «معرفی‌نامه لازم است» notice, and re-asking which insurer it
 * was for would be asking a question already answered.
 */

const FIELD = { display: 'flex', flexDirection: 'column', gap: 4 };
const LABEL = { fontSize: 11, fontWeight: 600, color: C.textMuted };
const ROW = { display: 'flex', gap: 10, flexWrap: 'wrap' };

const input = (invalid) => ({
  border: `1px solid ${invalid ? C.red : C.border}`,
  borderRadius: 8,
  padding: '6px 9px',
  fontSize: 12.5,
  fontFamily: 'inherit',
  background: C.card,
  color: C.textPrimary,
  width: '100%',
  boxSizing: 'border-box',
});

const DAY = 86_400_000;

// The same overlay the other modals in this app use, so a second
// dialog does not introduce a second idea of what a modal looks like.
const OVERLAY = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};

const SHEET = {
  background: C.card, borderRadius: 16, width: '100%', maxWidth: 460,
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden',
};

const HEAD = {
  padding: '14px 16px', borderBottom: `1px solid ${C.border}`,
  fontSize: 13.5, fontWeight: 700, color: C.textPrimary,
};

export default function ReferralLetterDialog({
  open,
  onClose,
  onSave,
  policies = [],
  policyId: initialPolicyId = null,
  appointmentId = null,
  saving = false,
}) {
  const { t, fromDisplayAmount } = useTranslation();
  const nameOf = useInsurerName();

  // Only بیمهٔ تکمیلی: basic insurers do not issue معرفی‌نامه in this model, and
  // offering one would invite a letter that authorises nothing.
  const supplementaries = useMemo(
    () => (policies || [])
      .filter((p) => p.kind === 'supplementary')
      .sort((a, b) => (a.priority || 0) - (b.priority || 0)),
    [policies],
  );

  const [form, setForm] = useState(() => blank(initialPolicyId, supplementaries));
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setForm(blank(initialPolicyId, supplementaries)); setError(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPolicyId, supplementaries.length]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setError(''); };

  const submit = async () => {
    const input_ = {
      policyId: form.policyId,
      letterNumber: form.letterNumber.trim(),
      issuedAt: form.issuedAt || null,
      validFrom: form.validFrom,
      validTo: form.validTo,
      scopeCatalogIds: [],
      scopeItemType: form.scopeItemType || null,
      approvedCeilingRial: form.ceilingToman === '' ? 0 : fromDisplayAmount(Number(form.ceilingToman)),
      patientSharePctOverride: form.sharePct === '' ? null : Number(form.sharePct),
      singleUse: form.singleUse,
      note: form.note.trim() || null,
      appointmentId: appointmentId || null,
    };
    const bad = validateReferralLetter(t, input_);
    if (bad) { setError(bad); return; }
    try {
      await onSave(input_);
      onClose();
    } catch (e) {
      setError(e?.message || t('common.errors.generic'));
    }
  };

  // Ctrl+S saves and Esc closes, through the ONE registry — a per-dialog window
  // listener is what makes two shortcuts fight (clinic-app/CLAUDE.md §4.2), and
  // it is also what puts them in the `?` overlay.
  useShortcut('ctrl+s', submit, {
    label: t('common.actions.save'),
    group: t('insurance.letter.title'),
    enabled: open && !saving,
    firesInInput: true,
  });
  useShortcut('esc', onClose, {
    label: t('common.actions.close'),
    group: t('insurance.letter.title'),
    enabled: open,
  });

  if (!open) return null;

  const noSupplementary = !supplementaries.length;

  const body = (
    <>
    {noSupplementary ? (
      <Alert tone="warning" data-testid="referral-letter-no-policy">
        {t('insurance.letter.errors.noSupplementary')}
      </Alert>
    ) : (
      <>
        <div style={FIELD}>
          <label style={LABEL} htmlFor="rl-policy">{t('insurance.groups.supplementary')}</label>
          <select
            id="rl-policy"
            style={input(false)}
            value={form.policyId || ''}
            onChange={(e) => set({ policyId: e.target.value })}
            data-testid="referral-letter-policy"
          >
            {supplementaries.map((p) => (
              <option key={p.id} value={p.id}>
                {nameOf(p.insurerId, p.insurerName)}
                {p.policyNumber ? ` — ${p.policyNumber}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={FIELD}>
          <label style={LABEL} htmlFor="rl-number">{t('insurance.letter.number')}</label>
          <input
            id="rl-number"
            style={input(false)}
            value={form.letterNumber}
            onChange={(e) => set({ letterNumber: e.target.value })}
            maxLength={64}
            autoFocus
            data-testid="referral-letter-number"
          />
        </div>

        <div style={ROW}>
          <div style={{ ...FIELD, flex: 1, minWidth: 150 }}>
            <label style={LABEL}>{t('insurance.letter.validFrom')}</label>
            <JalaliDatePicker
              value={form.validFrom || null}
              onChange={(ms) => set({ validFrom: ms || null })}
              data-testid="referral-letter-from"
            />
          </div>
          <div style={{ ...FIELD, flex: 1, minWidth: 150 }}>
            <label style={LABEL}>{t('insurance.letter.validTo')}</label>
            <JalaliDatePicker
              value={form.validTo || null}
              onChange={(ms) => set({ validTo: ms || null })}
              data-testid="referral-letter-to"
            />
          </div>
        </div>

        <div style={ROW}>
          <div style={{ ...FIELD, flex: 1, minWidth: 150 }}>
            <label style={LABEL} htmlFor="rl-ceiling">{t('insurance.letter.ceiling')}</label>
            <input
              id="rl-ceiling"
              style={input(false)}
              inputMode="numeric"
              value={form.ceilingToman}
              onChange={(e) => set({ ceilingToman: e.target.value.replace(/[^\d]/g, '') })}
              data-testid="referral-letter-ceiling"
            />
            <span style={{ fontSize: 10.5, color: C.textMuted }}>
              {t('insurance.letter.ceilingHint')}
            </span>
          </div>
          <div style={{ ...FIELD, flex: 1, minWidth: 130 }}>
            <label style={LABEL} htmlFor="rl-pct">{t('insurance.letter.sharePct')}</label>
            <input
              id="rl-pct"
              style={input(false)}
              inputMode="numeric"
              value={form.sharePct}
              onChange={(e) => set({ sharePct: e.target.value.replace(/[^\d]/g, '') })}
              data-testid="referral-letter-pct"
            />
            <span style={{ fontSize: 10.5, color: C.textMuted }}>
              {t('insurance.letter.sharePctHint')}
            </span>
          </div>
        </div>

        <div
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
          data-testid="referral-letter-single-use"
        >
          <Switch
            checked={form.singleUse}
            onCheckedChange={(on) => set({ singleUse: on })}
          />
          <span style={LABEL}>{t('insurance.letter.singleUse')}</span>
        </div>

        <div style={FIELD}>
          <label style={LABEL} htmlFor="rl-note">{t('insurance.letter.note')}</label>
          <input
            id="rl-note"
            style={input(false)}
            value={form.note}
            onChange={(e) => set({ note: e.target.value })}
            data-testid="referral-letter-note"
          />
        </div>
      </>
    )}

    {error ? (
      <Alert tone="danger" data-testid="referral-letter-error">{error}</Alert>
    ) : null}
    </>
  );

  const actions = (
    <>
      <Button variant="ghost" onClick={onClose} data-testid="referral-letter-cancel">
        {t('common.actions.cancel')}
      </Button>
      <Button
        onClick={submit}
        disabled={saving || noSupplementary}
        data-testid="referral-letter-save"
      >
        {t('common.actions.save')}
      </Button>
    </>
  );

  return (
    <AdaptiveModal
      open={open}
      onClose={onClose}
      title={t('insurance.letter.add')}
      snap={0.9}
      fullHeight={false}
      footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>{actions}</div>}
      data-testid="referral-letter-dialog"
      desktop={(
        <div
          style={OVERLAY}
          onClick={(e) => e.target === e.currentTarget && onClose()}
          data-testid="referral-letter-dialog"
        >
          <div style={SHEET} role="dialog" aria-modal="true" aria-label={t('insurance.letter.add')}>
            <div style={HEAD}>{t('insurance.letter.add')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, overflowY: 'auto' }}>
              {body}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>{actions}</div>
            </div>
          </div>
        </div>
      )}
    >
      {body}
    </AdaptiveModal>
  );
}

/**
 * A fresh form.
 *
 * The window defaults to today → thirty days, which is what Iranian supplementary
 * insurers actually issue; the desk overrides it when the letter says otherwise.
 * Guessing saves two date pickers on the common path and costs nothing when it is
 * wrong, because both fields are visible.
 */
function blank(policyId, supplementaries) {
  const now = Date.now();
  return {
    policyId: policyId || supplementaries[0]?.id || '',
    letterNumber: '',
    issuedAt: now,
    validFrom: now,
    validTo: now + 30 * DAY,
    scopeItemType: null,
    ceilingToman: '',
    sharePct: '',
    singleUse: true,
    note: '',
  };
}
